import type { MessageDescriptor } from '@lingui/core';
import { useLocation } from 'react-router-dom';
import { useTier } from '../contexts/TierContext';
import Layout from './Layout';
import MaxUpsell from './MaxUpsell';

interface TierGateProps {
  children: React.ReactNode;
  /** Feature name for the upsell panel, e.g. msg`Privacy Agents`. */
  feature: MessageDescriptor;
  /** One line about the feature for the upsell panel. */
  description: MessageDescriptor;
}

/**
 * Route wrapper for Max-only pages (EPIC-07 7.2, reworked for EPIC-15). Reads
 * the shared TierContext (one account fetch for the whole portal) and renders
 * the page only when effectiveTier is 'max'; free (Pro) users see the
 * MaxUpsell conversion panel instead. The server-side 403 MAX_TIER_REQUIRED
 * remains the real gate; this keeps the portal honest and friendly.
 */
export default function TierGate({ children, feature, description }: TierGateProps) {
  const { isMax, isAdmin, loading } = useTier();
  const location = useLocation();

  if (loading) {
    return (
      <Layout>
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </Layout>
    );
  }

  if (!isMax) {
    return (
      <Layout>
        {/* The locked route is the attribution: it follows the customer into
            the checkout modal and onto the trial-call link, so sales can see
            which feature they were trying to reach. */}
        <MaxUpsell
          feature={feature}
          description={description}
          isAdmin={isAdmin}
          source={`gate${location.pathname}`}
        />
      </Layout>
    );
  }

  return <>{children}</>;
}
